import {ExternalCompletionBadge, ExternalCompletionNote} from "./ExternalCompletion";
import {PayinStageBadge} from "./PayinParts";
import React, {useState} from "react";
import type {Payment, PaymentDirection} from "../types";
import {formatAmount, formatCard, formatDate, formatFixedRate, statusClass, traderAmountRangeText, traderRequisites} from "../utils";
import {CountdownBadge, ORDER_DEADLINE_LABEL, SOURCE_DEADLINE_LABEL} from "./CountdownBadge";
import {ElapsedBadge} from "./ElapsedBadge";
import {closedAtSourceNotice, exchangePayoutOpen, isClaimTimeoutAppeal} from "./payment-details";
import {isExternalWorkflow, isPayinWorkflow} from "./payment-details/workflow";

type PaymentRowVariant = "available" | "owned";

interface PaymentRowProps {
    payment: Payment;
    direction: PaymentDirection;
    variant?: PaymentRowVariant;
    claiming?: boolean;
    selected?: boolean;
    onClaim?: (orderId: string) => void;
    onOpen?: (payment: Payment) => void;
    onExpire?: () => void;
}

export function PaymentRow({
                               payment,
                               direction,
                               variant = "owned",
                               claiming = false,
                               selected = false,
                               onClaim,
                               onOpen,
                               onExpire,
                           }: PaymentRowProps): JSX.Element {
    const [expanded, setExpanded] = useState(false);
    const p = payment;
    const claimTimeoutAppeal = isClaimTimeoutAppeal(p);
    const isAvailable = variant === "available";
    const requisites = traderRequisites(p, isAvailable);
    const bankLabel = p.clientBank || p.bankId;
    const completedExternalPayment = p.status.toLowerCase() === "success" && Boolean(p.externalCompletion);
    const displayedAmount = completedExternalPayment ? p.amountReal ?? p.amount : p.amountRemaining ?? p.amount;
    const hasPartialRemainder = !completedExternalPayment && p.amountRemaining != null && p.amountRemaining !== p.amount;
    const orderCreatedAt = p.orderCreatedAt || p.createdAt;
    const poolFreezeAt = p.firstClaimedAt ?? (!isAvailable ? p.createdAt : null);
    const status = p.status.toLowerCase();
    const isFinal = status === "success" || status === "cancelled";
    const isProcessing = status === "pending" || status === "partially_filled" || status === "appeal";
    // Приходит только на заявках из пула — у взятых заявок вопрос «можно ли взять»
    // уже не стоит, и бэкенд там всегда отдаёт false.
    const prepayBlocked = isAvailable && p.prepayBlocked === true;
    const prepayBlockedReason = p.prepayShortBy
        ? `Мерчант на предоплате: не хватает ${p.prepayShortBy} USDT. Заявка станет доступной после пополнения.`
        : "Мерчант на предоплате: доступный остаток исчерпан. Заявка станет доступной после пополнения.";
    // Срок площадки. Он начал течь, когда заявка появилась У НИХ, — не когда
    // доехала до нас и не когда её взял трейдер. Логика внешняя, и таймер мы
    // берём их же, поэтому обратный отсчёт висит всю жизнь заявки: и в пуле, и
    // в работе. Иначе трейдер выбирает из пула вслепую и узнаёт цену решения
    // уже внутри работы — а «TOTAL 02:24:20» рядом означает возраст, то есть
    // ровно противоположное.
    //
    // Наличие срока само по себе больше не выбирает сценарий: источником истины
    // служит неизменяемый workflow заявки.
    const sourceDeadline = isExternalWorkflow(p.workflowKind) ? (p.claimNotAfter ?? null) : null;
    // Ноль на этом таймере больше НЕ выключает кнопку. Срок площадки перестал
    // убивать заявку 19.08.2026 (В-29): 275 карточек прода из 425 со сроком
    // закрылись в успех уже после него, и гейт взятия по сроку не отказывает
    // ни разу. Выключенная кнопка была бы ровно той ложью, которой мы избегаем в
    // обратную сторону, — только теперь запрещал бы фронт, а сервер разрешал.
    //
    // Сам таймер и красная отметка «срок вышел» остаются: срок остался сроком и
    // мотивирует закрыть быстрее (В-27). Обновление списка по нулю тоже
    // остаётся (onExpire ниже) — карточка могла измениться и помимо срока.

    // Собственный срок обычной заявки: момент, к которому она должна быть
    // оплачена, считая от появления в пуле. Показывается ИМЕННО в пуле — ради
    // этого поле туда и приехало: трейдер обязан видеть остаток до нажатия,
    // иначе он выбирает вслепую и узнаёт цену решения уже внутри работы.
    //
    // У взятой заявки тот же момент рисует бейдж брони ниже, и второй такой же
    // здесь был бы не подтверждением, а рябью в глазах.
    //
    // У заявки кабинета поле пустое: её срок абсолютный и едет claimNotAfter.
    const orderDeadline = isAvailable ? (p.expiresAt ?? null) : null;
    // Тот же срок у взятой заявки. Показывается на месте статусного чипа и
    // ВМЕСТО него: подпись у отсчёта — то же слово PENDING, и оставить рядом
    // второй жёлтый чип с ним же значило бы написать статус дважды.
    //
    // Заявки кабинета сюда не попадают: их срок рисует бейдж PAYLORD выше, а
    // статус у такой карточки остаётся своим чипом.
    const workDeadline = !isAvailable && status === "pending" && !sourceDeadline && !claimTimeoutAppeal
        ? (p.expiresAt ?? null)
        : null;

    // Заявка, которую видно, но брать нельзя. Раньше такие просто не
    // показывались, и «заявок нет» было неотличимо от «заявки есть, но взять их
    // нечем» — для контура это худший из двух отказов.
    //
    // Причин теперь две, и путать их нельзя: одна про заявку площадки, другая
    // про вышедший срок обычной заявки. Слово приходит с сервера кодом, текст
    // остаётся здесь — причина одна и та же для всех таких заявок, и повторять
    // её в каждой строке ответа незачем.
    const claimBlocked = isAvailable && p.claimable === false;
    const claimBlockedByDeadline = p.claimBlockedReason === "order_deadline_too_close";
    const claimBlockedReason = claimBlockedByDeadline
        ? "Срок заявки вышел. Заявка закроется сама, взять её уже нельзя."
        : "Заявка внешней площадки. Видна для контроля, взять её нельзя: у системы нет обратного пути на площадку, " +
          "и о взятии сообщить некому. Станет доступной, когда обратный путь включат.";
    const claimBlockedBadge = claimBlockedByDeadline ? "Срок вышел" : "Только просмотр";

    const handleClaim = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        onClaim?.(p.orderId);
    };

    return (
        <div
            data-order-id={p.orderId}
            className={`payment-row ${direction} ${variant} ${selected ? "selected" : ""} ${p.merchantIsVip ? "vip" : ""} ${p.merchantOverLimit ? "over-limit" : ""} ${prepayBlocked ? "prepay-blocked" : ""} ${claimBlocked ? "claim-blocked" : ""}`}
            onClick={() => {
                if (!isAvailable && onOpen) {
                    onOpen(p);
                    return;
                }
                setExpanded(!expanded);
            }}
        >
            <div className="payment-row-head">
                <div className="payment-head-info">
                    <div className="payment-direction-indicator">
                        <span className="payment-direction-label">{directionLabel(direction)}</span>
                    </div>
                    <span className="payment-merchant-name" title={p.merchantName || undefined}>
                        {p.merchantName || "—"}
                    </span>
                    {p.workflowKind === "standard_payin" && !isFinal && <PayinStageBadge issued={Boolean(p.payinRequisites)}/>}
                    {p.merchantOverLimit && (
                        <span
                            className="payment-debt-badge"
                            title="Мерчант превысил лимит долга — работать с заявкой или нет, решаете вы"
                        >
                            {p.merchantDebtExceededBy
                                ? `Долг превышен на ${p.merchantDebtExceededBy} USDT`
                                : "Долг"}
                        </span>
                    )}
                    {/* В отличие от «Долга» это не сигнал, а запрет: взять такую
                        заявку нельзя, бэкенд вернёт 409. */}
                    {prepayBlocked && (
                        <span className="payment-prepay-badge" title={prepayBlockedReason}>
                            {p.prepayShortBy
                                ? `Нет предоплаты: −${p.prepayShortBy} USDT`
                                : "Нет предоплаты"}
                        </span>
                    )}
                    {/* Тоже запрет, а не сигнал: кнопка «Взять» у такой заявки
                        выключена заранее и нажаться не может. */}
                    {claimBlocked && (
                        <span className="payment-claim-blocked-badge" title={claimBlockedReason}>
                            {claimBlockedBadge}
                        </span>
                    )}
                    {/* Системная timeout-апелляция обычной заявки блокирует
                        возврат в пул. Ручной спор остаётся информационной меткой
                        и сохраняет прежнюю семантику. */}
                    {claimTimeoutAppeal ? (
                        <span
                            className="payment-appeal-badge"
                            title="Срок истёк: заявка остаётся за вами до завершения, окончательной отмены или решения администратора."
                        >
                            Апелляция
                        </span>
                    ) : p.hasAppeal && (
                        <span
                            className="payment-appeal-badge"
                            title="По заявке открыт спор — её разбирает админ. Работать с ней можно как обычно: закрывать и возвращать в пул."
                        >
                            Спор
                        </span>
                    )}
                    {/* Рубли по заявке-обмену закрыты, а USDT клиенту ещё не
                        отправлены. Строка остаётся в «В работе» именно из-за
                        этого, и без бейджа она читалась бы как успешная,
                        по которой почему-то ничего не надо делать. */}
                    {exchangePayoutOpen(p) && (
                        <span
                            className="payment-appeal-badge"
                            title="Рубли получены. Осталось отправить USDT на адрес клиента и вставить hash перевода."
                        >
                            USDT-этап
                        </span>
                    )}
                    {/* Заявку закрыла площадка, пока она была открыта у трейдера
                        (В-31). Действий по ней нет; бейдж нужен, чтобы это было
                        видно в списке, а не только внутри карточки. */}
                    {p.externalClosedAtSource && (
                        <span className="payment-claim-blocked-badge" title={closedAtSourceNotice(p)}>
                            Закрыта на площадке
                        </span>
                    )}
                    {/* Курс фикс-заявки виден ещё до взятия: трейдер решает, брать
                        ли заявку, зная её курс — менять он его потом не сможет. */}
                    {p.rateFixed && (
                        <span
                            className="payment-rate-badge"
                            title={`Курс зафиксирован при создании заявки: ${formatFixedRate(p) ?? p.rateFixed}`}
                        >
                            {p.rateFixed} ₽/USDT
                        </span>
                    )}
                </div>
                <div className="elapsed-badges" aria-label="Payment age timers">
                    {/* Закрытой заявке остаток времени не нужен: она уже
                        закончилась, и «срок вышел» на успешно оплаченной читалось
                        бы как отказ. Возрастные таймеры рядом в этот момент
                        замирают — этот просто уходит. */}
                    {/* Срок самой заявки — в том же слоте, что и срок площадки:
                        у одной заявки он ровно один, и две ветки взаимно
                        исключены по построению. */}
                    {orderDeadline && !isFinal && (
                        <CountdownBadge
                            label={ORDER_DEADLINE_LABEL}
                            expiresAt={orderDeadline}
                            onExpire={onExpire}
                            expiredText="срок вышел"
                            hint="Срок заявки: сколько осталось до момента, к которому она должна быть оплачена"
                            className="order-deadline"
                            withSeconds
                        />
                    )}
                    {sourceDeadline && !isFinal && (
                        <CountdownBadge
                            label={SOURCE_DEADLINE_LABEL}
                            expiresAt={sourceDeadline}
                            onExpire={onExpire}
                            expiredText="срок вышел"
                            hint="Срок площадки: сколько осталось до момента, к которому считает её кабинет"
                            className="source-deadline"
                            withSeconds
                        />
                    )}
                    <ElapsedBadge
                        label="TOTAL"
                        startAt={orderCreatedAt}
                        frozen={isFinal}
                        freezeAt={p.updatedAt}
                    />
                    <ElapsedBadge
                        label="POOL"
                        startAt={orderCreatedAt}
                        frozen={poolFreezeAt != null}
                        freezeAt={poolFreezeAt}
                    />
                    {!isAvailable && (
                        <ElapsedBadge
                            label="WORK"
                            startAt={p.createdAt}
                            frozen={!isProcessing}
                            freezeAt={p.updatedAt}
                        />
                    )}
                </div>
            </div>

            <div className="payment-row-body">
                <div className="payment-amount-line">
                    <span className="payment-amount">
                        {traderAmountRangeText(p) ?? formatAmount(displayedAmount)}{" "}
                        <span className="payment-currency">RUB</span>
                    </span>
                    {p.merchantIsVip && <span className="payment-vip-badge">VIP</span>}
                    {hasPartialRemainder && (
                        <span className="payment-original-amount">из {formatAmount(p.amount)} RUB</span>
                    )}
                </div>
                <div className="payment-requisites-row">
                    <span className="payment-requisites">
                        <span className="payment-card">{requisites.card}</span>
                        {bankLabel && <span className="payment-bank"> · {bankLabel}</span>}
                    </span>
                    <div className="payment-row-action">
                        {isAvailable ? (
                            <button
                                className="btn btn-primary claim-button"
                                disabled={claiming || prepayBlocked || claimBlocked}
                                title={
                                    claimBlocked
                                        ? claimBlockedReason
                                        : prepayBlocked
                                          ? prepayBlockedReason
                                          : undefined
                                }
                                onClick={handleClaim}
                            >
                                {claiming
                                    ? "Taking..."
                                    : claimBlockedByDeadline
                                      ? "Срок вышел"
                                      : claimBlocked
                                        ? "Просмотр"
                                        : "Take"}
                            </button>
                        ) : (
                            <>
                                {/* Отсчёт идёт только у живой брони: спорный платёж
                                    заморожен и сам не истекает, а у заявки кабинета
                                    бронь и есть срок площадки — он уже нарисован
                                    бейджем PAYLORD выше.

                                    Плашка одна на два факта. Формат тот же, что в
                                    пуле (withSeconds): один таймер — одна запись,
                                    иначе 07:02 здесь и 00:07:02 там читаются как
                                    разные величины. */}
                                {claimTimeoutAppeal ? (
                                    <span className="payment-status appeal">Срок истёк · Апелляция</span>
                                ) : workDeadline ? (
                                    <CountdownBadge
                                        label={ORDER_DEADLINE_LABEL}
                                        expiresAt={workDeadline}
                                        onExpire={onExpire}
                                        expiredText="срок вышел"
                                        hint="Срок заявки: сколько осталось до момента, к которому она должна быть оплачена"
                                        withSeconds
                                    />
                                ) : (
                                    <span className={`payment-status ${statusClass(p.status)}`}>
                                        {completedExternalPayment ? "Local success" : p.status || "unknown"}
                                    </span>
                                )}
                            </>
                        )}
                    </div>
                </div>
                {/* Paylord's live SBP orders carry no client name at all, and an empty
                    string here left a nameless blank line under the requisites. The dash
                    is display only: the order really has no name, and writing one into
                    the data would be indistinguishable from a client called that. */}
                <ExternalCompletionBadge completion={p.externalCompletion}/>
                <ExternalCompletionNote completion={p.externalCompletion}/>
                <span className="payment-person" title={p.clientFio || undefined}>{p.clientFio || "—"}</span>
            </div>

            {expanded && (
                <div className="payment-details">
                    <div className="detail-grid">
                        {!isAvailable && <DetailItem label="Payment ID" value={p.paymentId} mono/>}
                        <DetailItem label="Order ID" value={p.orderId} mono/>
                        <DetailItem label="Status" value={p.status}/>
                        <ExternalCompletionBadge completion={p.externalCompletion}/>
                        <DetailItem label="Remote ID" value={p.remoteId} mono/>
                        <DetailItem label="Bank ID" value={p.bankId}/>
                        <DetailItem label="Client bank" value={p.clientBank}/>
                        <DetailItem label="Client" value={p.clientFio}/>
                        {/* Те же реквизиты, что и в свёрнутой строке, и решение
                            о закрытии у них одно на двоих — см. traderRequisites. */}
                        <DetailItem label="Client phone" value={requisites.phone} mono/>
                        <DetailItem label="Client card" value={requisites.card} mono/>
                        {isPayinWorkflow(p.workflowKind) && !isAvailable && (
                            <DetailItem
                                label="Реквизиты приёма"
                                value={p.payinRequisites ? `${p.payinRequisites.bankName} · ${formatCard(p.payinRequisites.cardNumber)}` : "не заполнены"}
                                mono={Boolean(p.payinRequisites)}
                            />
                        )}
                        <DetailItem label="Merchant" value={p.merchantName ?? "—"}/>
                        <DetailItem label="Merchant VIP" value={p.merchantIsVip ? "yes" : "no"}/>
                        {p.merchantOverLimit && (
                            <DetailItem
                                label="Долг мерчанта"
                                value={p.merchantDebtExceededBy ? `превышен на ${p.merchantDebtExceededBy} USDT` : "превышен"}
                            />
                        )}
                        {prepayBlocked && (
                            <DetailItem
                                label="Предоплата мерчанта"
                                value={p.prepayShortBy ? `не хватает ${p.prepayShortBy} USDT` : "остаток исчерпан"}
                            />
                        )}
                        {claimBlocked && (
                            <DetailItem
                                label="Взятие"
                                value={
                                    claimBlockedByDeadline
                                        ? "отключено — срок заявки вышел"
                                        : "отключено — заявка внешней площадки"
                                }
                            />
                        )}
                        {!isAvailable && <DetailItem label="Amount real" value={formatAmount(p.amountReal)}/>}
                        {!isAvailable && <DetailItem label="Receipt ID" value={p.receiptId} mono/>}
                        <DetailItem label="Created" value={formatDate(p.createdAt)}/>
                        <DetailItem label="Order created" value={formatDate(orderCreatedAt)}/>
                        <DetailItem label="First claimed" value={formatDate(p.firstClaimedAt)}/>
                        <DetailItem label="Updated" value={formatDate(p.updatedAt)}/>
                    </div>
                </div>
            )}
        </div>
    );
}

function directionLabel(direction: PaymentDirection): string {
    return direction === "in" ? "Входящая" : "Исходящая";
}

interface DetailItemProps {
    label: string;
    value: string | number | null | undefined;
    mono?: boolean;
}

function DetailItem({label, value, mono}: DetailItemProps): React.JSX.Element {
    return (
        <div className="detail-item">
            <span className="detail-label">{label}</span>
            <span className={`detail-value${mono ? " mono" : ""}`}>
                {value == null || value === "" ? "-" : String(value)}
            </span>
        </div>
    );
}
